<template>
  <div class="recipe-suggested text-center">
    <!-- Title -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h2 class="m-0">Что приготовить сегодня?</h2>
      <button
        @click="loadNextSuggestedRecipe"
        class="btn next"
        :disabled="isLoading || !recipe"
        title="Следующее предложение"
      >
        <i class="bi bi-arrow-repeat"></i>
      </button>
    </div>

    <!-- Image or placeholder  -->
    <div
      class="image-box mb-3"
      :style="{
        transform: `translateX(${translateX}px)`,
        transition: isSwiping ? 'none' : 'transform 0.3s ease-out',
      }"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
      @touchcancel="handleTouchEnd"
    >
      <!-- Image -->
      <img
        v-if="imageUrl && !imageError"
        class="image-recipe"
        :class="{ clickable: recipe && recipe.id }"
        :src="imageUrl"
        :alt="recipe.name"
        @load="imageLoaded = true"
        @error="imageError = true"
        @click="navigateToRecipe"
        role="button"
        tabindex="0"
      />

      <!-- Placeholder or Spinner for loading image -->
      <div
        v-else
        class="image-box image-placeholder"
        :style="
          bacgroundImageUrl
            ? { backgroundImage: `url(${bacgroundImageUrl})` }
            : ''
        "
      >
        <!-- Spinner for loading recipe and image -->
        <div
          v-if="isLoading || (!imageLoaded && !imageError)"
          class="spinner-holder"
        >
          <div
            class="spinner-border text-success mb-2"
            role="status"
            style="width: 2.5rem; height: 2.5rem"
          >
            <span class="visually-hidden">Загрузка...</span>
          </div>
          <!-- <span class="text-muted">Загрузка...</span> -->
        </div>

        <!-- Placeholder -->
        <div v-else>
          <div v-if="!isLoading && !imageLoaded">
            <i
              class="bi bi-card-image"
              style="font-size: 3rem; color: #aaa"
            ></i>
            <!-- <span class="mt-2" style="color: #aaa">Изображение недоступно</span> -->
          </div>
        </div>
      </div>
    </div>

    <!-- Show suggested recipe when Loaded -->
    <div v-if="recipe">
      <!-- Recipe Name -->
      <div class="d-flex justify-content-between align-items-start mb-2">
        <h3
          :class="{ clickable: recipe && recipe.id }"
          @click="navigateToRecipe"
          role="button"
          tabindex="0"
        >
          {{ recipe.name }}
        </h3>

        <!-- Buttons container -->
        <div class="d-flex align-items-center">
          <!-- Selection Button Component -->
          <RecipeSelection
            v-if="recipe && recipe.id"
            :recipe-id="recipe.id"
            class="me-2"
          />
          <!-- Interest Button Component -->
          <RecipeInterest
            v-if="recipe && recipe.id"
            :recipe-id="recipe.id"
            @interest-toggled="handleInterestToggledInSuggested"
            class="me-2"
          />
          <!-- Like Button Component -->
          <RecipeLike
            v-if="recipe && recipe.id"
            :recipe-id="recipe.id"
            @like-toggled="handleLikeToggledInSuggested"
          />
        </div>
      </div>

      <RecipeRating
        v-if="recipe && recipe.id"
        :recipe-id="recipe.id"
        class="mb-2 mt-2 justify-content-center"
      />
    </div>
  </div>
</template>

<script>
import RecipeLike from "./RecipeLike.vue";
import RecipeRating from "./RecipeRating.vue";
import RecipeInterest from "./RecipeInterest.vue";
import RecipeSelection from "./RecipeSelection.vue";
const CMD_RECIPE_SUGGEST = "recipe.suggest";

export default {
  name: "RecipeSuggested",
  props: {
    mealType: {
      type: String,
      required: true,
    },
  },

  emits: ["suggestion-type"],

  data() {
    return {
      recipe: null,
      suggestionIDs: [], // To store the list of suggested recipe IDs
      isLoading: true,
      error: null,
      imageUrl: null,
      imageLoaded: false,
      imageError: false,
      // Object to hold arrays of background image URLs for the placeholder,
      // keyed by mealType
      placeholderBackgroundsByMealType: {
        // Replace these with your actual URLs or local paths
        // For now, one image per meal type as requested
        // TODO: Save it in database and create admin component to manage this
        // list
        завтрак: [
          "Что приготовить сегодня на завтрак - 1",
          "Что приготовить сегодня на завтрак - 2",
          "Что приготовить на завтрак - 3",
          "Что приготовить сегодня на завтрак - 4",
          "Что приготовить сегодня на завтрак - 5",
          "Что приготовить на завтрак - 6",
        ],
        обед: [
          "Что приготовить сегодня на обед - 1",
          "Что приготовить сегодня на обед - 2",
          "Что приготовить сегодня на обед - 3",
          "Что приготовить на обед - 4",
        ],
        ужин: [
          "Что приготовить сегодня на ужин - 1",
          "Что приготовить сегодня на ужин - 2",
          "Что приготовить сегодня на ужин - 3",
          "Что приготовить сегодня на ужин - 4",
        ],
      },
      bacgroundImageUrl: null,
      retryTimeout: null,
      retryDelay: 7000,
      // Swipe functionality
      touchStartX: 0,
      translateX: 0,
      isSwiping: false,
      swipeDirection: null, // 'horizontal' or 'vertical'
    };
  },
  components: {
    RecipeLike,
    RecipeRating,
    RecipeInterest,
    RecipeSelection,
  },
  async mounted() {
    // Set meal type based on time, which will also trigger background selection
    // via watcher
    this.setDefaultMealTypeBasedOnTime();
    if (this.mealType) {
      // Select initial background based on mealType
      this.selectRandomBackgroundImage();
      await this.loadSuggestedRecipeAndImage();
    }
  },

  unmounted() {
    // Clear the retry timer when the component is destroyed
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  },

  watch: {
    "$globalState.apiUrl"(newApiUrl) {
      if (newApiUrl && this.recipe && !this.imageUrl && this.imageError) {
        // If API URL becomes available, and we have a recipe,
        // and an image wasn't loaded (or failed previously due to no URL), try fetching image again.
        console.log(
          "RecipeSuggested: Global API URL available, attempting to fetch image."
        );
        this.fetchAndSetImageUrl();
      }
    },
    mealType(newMealType, oldMealType) {
      if (newMealType && newMealType !== oldMealType) {
        console.log(
          `RecipeSuggested: mealType prop changed to: ${newMealType}. Reloading.`
        );
        this.selectRandomBackgroundImage();
        localStorage.setItem(`cooksyRecipeOffset_${newMealType}`, "0");
        this.loadSuggestedRecipeAndImage();
      }
    },
  },

  methods: {
    async loadSuggestedRecipeAndImage() {
      // Clear any pending retry timer if a new load is initiated manually
      if (this.retryTimeout) {
        clearTimeout(this.retryTimeout);
        this.retryTimeout = null;
      }

      this.isLoading = true;
      this.error = null;
      // this.recipe = null;
      this.resetImageState();
      this.selectRandomBackgroundImage();

      try {
        let currentOffset = parseInt(
          localStorage.getItem(`cooksyRecipeOffset_${this.mealType}`) || "0",
          10
        );

        // Get current date in YYYY-MM-DD format
        const today = new Date().toISOString().slice(0, 10);

        // The offset is managed by parent or incremented on error/invalid data below
        const command = `${CMD_RECIPE_SUGGEST}/${
          this.mealType
        }/${currentOffset}/${encodeURIComponent(today)}`;

        // Send the command with a small 5 seconds timeout
        const rawRecipeData = await this.sendCmd(command, null, 5000);
        const response = JSON.parse(rawRecipeData);

        const parsedRecipeData = response.recipe;
        const suggestionIDs = response.suggestions;
        const newOffset = response.offset;
        const isRandom = response.is_random;

        // Check if parsedRecipeData is valid
        if (
          parsedRecipeData &&
          typeof parsedRecipeData.name === "string" &&
          parsedRecipeData.name.trim() !== ""
        ) {
          this.recipe = parsedRecipeData;
          this.suggestionIDs = suggestionIDs || [];

          // Emit event to parent about the type of suggestion
          this.$emit("suggestion-type", { isRandom: isRandom });

          // If the server returns a corrected offset, update it
          localStorage.setItem(
            `cooksyRecipeOffset_${this.mealType}`,
            String(newOffset)
          );
          await this.fetchAndSetImageUrl();
          this.isLoading = false; // Success
        } else {
          console.warn(
            `Parsed recipeData is invalid for ${this.mealType} at offset ${currentOffset}. Parsed data:`,
            parsedRecipeData
          );

          // If we get invalid data, it might be the end of a list or an error.
          // Let's try to loop back to the start.
          if (currentOffset > 0) {
            localStorage.setItem(`cooksyRecipeOffset_${this.mealType}`, "0");
            await this.loadSuggestedRecipeAndImage();
          } else {
            // We are already at offset 0 and got invalid data.
            // This is a more serious issue, schedule a retry.
            console.error(`No valid recipes available for "${this.mealType}".`);
            this.scheduleRetry();
          }
        }
      } catch (err) {
        const errorMessage = err.message || String(err);
        if (errorMessage.includes("get user data: user data is not UserData")) {
          console.error(
            "RecipeSuggested: Authentication session lost. Reloading the application."
          );
          window.location.reload();
        } else if (errorMessage.includes("insufficient_quota")) {
          console.error("OpenAI quota exceeded. Not retrying.", err);
          this.error = "AI-помощник временно недоступен. Попробуйте позже.";
          this.isLoading = false; // Stop the spinner
        } else {
          console.error("Error fetching suggested recipe:", err);
          this.scheduleRetry();
        }
      }
    },

    scheduleRetry() {
      this.recipe = null; // Ensure no stale recipe is shown
      this.retryTimeout = setTimeout(() => {
        console.log(
          `Retrying to load suggested recipe after ${this.retryDelay}ms...`
        );
        this.loadSuggestedRecipeAndImage();
      }, this.retryDelay);
      // isLoading remains true, so the spinner will continue
    },

    /**
     * Resets the image state to its initial values.
     * This is done before fetching a new image, and when no recipe is set.
     *
     * Resets the following states:
     * - imageUrl: set to null
     * - imageLoaded: set to false
     * - imageError: set to false
     */
    resetImageState() {
      // Reset image url to null
      this.imageUrl = null;

      // Set imageLoaded to false
      this.imageLoaded = false;

      // Set imageError to false
      this.imageError = false;
    },

    // Make fetchAndSetImageUrl async to use await
    async fetchAndSetImageUrl() {
      if (!this.recipe || !this.recipe.name) {
        this.resetImageState(); // Ensure image state is cleared if no recipe
        return;
      }

      this.resetImageState(); // Reset before fetching new image

      /**
       * Retrieves the image URL for the current recipe name.
       *
       * If the global API URL is not yet available, this function will retry.
       * @returns {Promise<string|null>} A promise that resolves with the image
       * URL or null if an error occurs.
       */
      const getImageUrlPromise = () => {
        return new Promise((resolve) => {
          /**
           * Recursive function to check when API URL is available
           * and set the image url for the suggested recipe
           */
          const checkApiUrl = () => {
            const baseUrl = this.$globalState.apiUrl;
            if (baseUrl === null || baseUrl === "") {
              // If API URL is not available, try again after a short delay
              console.log(
                "RecipeSuggested: Global API URL not yet available. Retrying in a few milliseconds..."
              );
              return requestAnimationFrame(checkApiUrl);
            }

            // Reset the bacgroundImageUrl and Resolve the promise with the
            // constructed image URL
            console.log(
              "RecipeSuggested: Using global API URL for image:",
              baseUrl
            );
            this.bacgroundImageUrl = null;
            const encodedRecipeName = encodeURIComponent(this.recipe.name);
            const constructedImageUrl = `${baseUrl}/image.get/${encodedRecipeName}`;
            resolve(constructedImageUrl);
          };
          checkApiUrl();
        });
      };

      try {
        this.imageUrl = await getImageUrlPromise();
        console.log("RecipeSuggested: Image URL set to:", this.imageUrl);
      } catch (err) {
        // Reset image url and set error.
        // Error is already logged by getImageUrlPromise or its try/catch
        this.imageError = true;
        this.imageUrl = null;
      }
    },

    handleLikeToggledInSuggested(newIsLiked, recipeId) {
      console.log(
        `RecipeSuggested: Like status for recipe ${recipeId} changed to ${newIsLiked}`
      );
    },

    handleInterestToggledInSuggested(newIsInterested, recipeId) {
      console.log(
        `RecipeSuggested: Interest status for recipe ${recipeId} changed to ${newIsInterested}`
      );
    },

    // Sets the default meal type based on the current time and loads its first suggestion
    setDefaultMealTypeBasedOnTime() {
      // This logic is now in RecipeMeal.vue.
      // RecipeSuggested will receive the mealType as a prop.
      // The initial load is handled by the watcher for mealType or in mounted.
      if (this.mealType) {
        console.log(
          "RecipeSuggested: setDefaultMealTypeBasedOnTime - mealType already set by prop:",
          this.mealType
        );
      } else {
        console.warn(
          "RecipeSuggested: setDefaultMealTypeBasedOnTime - mealType prop is not yet available."
        );
        // Potentially, do nothing here and wait for the prop to be passed.
      }
    },

    // Loads the next suggested recipe for the currently selected meal type
    async loadNextSuggestedRecipe() {
      if (this.isLoading || !this.recipe || !this.mealType) {
        console.log(
          "Next button conditions not met (loading, no recipe, or no meal type)."
        );
        return;
      }

      let currentOffset = parseInt(
        localStorage.getItem(`cooksyRecipeOffset_${this.mealType}`) || "0",
        10
      );
      localStorage.setItem(
        `cooksyRecipeOffset_${this.mealType}`,
        String(currentOffset + 1) // We just increment, the server will handle looping
      );
      // loadSuggestedRecipeAndImage will set isLoading = true at its beginning
      await this.loadSuggestedRecipeAndImage();
    },

    // Loads the previous suggested recipe for the currently selected meal type
    async loadPreviousSuggestedRecipe() {
      if (this.isLoading || !this.mealType) {
        console.log(
          "Previous swipe conditions not met (loading or no meal type)."
        );
        return;
      }

      let currentOffset = parseInt(
        localStorage.getItem(`cooksyRecipeOffset_${this.mealType}`) || "0",
        10
      );

      let newOffset = currentOffset - 1;
      if (newOffset < 0) {
        // Loop to the end of the list if we have it
        newOffset =
          this.suggestionIDs.length > 0 ? this.suggestionIDs.length - 1 : 0;
      }

      if (newOffset >= 0) {
        localStorage.setItem(
          `cooksyRecipeOffset_${this.mealType}`,
          String(newOffset)
        );
        await this.loadSuggestedRecipeAndImage();
      }
    },

    // Navigates to the full recipe page
    selectRandomBackgroundImage() {
      return new Promise((resolve) => {
        if (
          !this.mealType ||
          !this.placeholderBackgroundsByMealType[this.mealType]
        ) {
          console.warn(
            "RecipeSuggested: Cannot select background, mealType not set or no images for it."
          );
          this.bacgroundImageUrl = null;
          resolve(null);
          return;
        }

        // Get images for current mealType
        const imagesForCurrentMealType =
          this.placeholderBackgroundsByMealType[this.mealType];
        if (imagesForCurrentMealType.length === 0) {
          this.bacgroundImageUrl = null;
          resolve(null);
          return;
        }

        /**
         * Recursive function to check when API URL is available
         * and set the background image for the suggested recipe
         */
        const checkApiUrl = () => {
          const baseUrl = this.$globalState.apiUrl;
          if (baseUrl === null || baseUrl === "") {
            // If API URL is not available, try again after a short delay
            return requestAnimationFrame(checkApiUrl);
          }

          // Select a random background image for the current meal type
          const randomIndex = Math.floor(
            Math.random() * imagesForCurrentMealType.length
          );
          const encodedRecipeName = encodeURIComponent(
            imagesForCurrentMealType[randomIndex]
          );

          // Set the background image URL after 100ms delay and resolve the
          // promise
          setTimeout(() => {
            // Set the background image URL if imageUrl is not already set
            if (!this.imageUrl) {
              this.bacgroundImageUrl = `${baseUrl}/image.get/${encodedRecipeName}`;
              console.log(
                `RecipeSuggested: Selected background for ${this.mealType}:`,
                this.bacgroundImageUrl
              );
            }

            // Resolve the promise
            resolve(this.bacgroundImageUrl);
          }, 350);
        };
        checkApiUrl();
      });
    },
    navigateToRecipe() {
      if (this.recipe && this.recipe.id) {
        localStorage.setItem(
          "lastViewedRecipe",
          JSON.stringify({
            from: "suggested",
            id: this.recipe.id,
          })
        );
        window.location.hash = `/recipes/${this.recipe.id}`;
      } else {
        console.warn("Cannot navigate: recipe or recipe.id is missing.");
        // Optionally, provide user feedback if navigation is not possible
      }
    },

    // --- Swipe Functionality ---

    handleTouchStart(event) {
      if (this.isLoading || !this.recipe) return;
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
      this.isSwiping = true;
      this.swipeDirection = null; // Reset direction on new touch
    },

    handleTouchMove(event) {
      if (!this.isSwiping || !event.touches.length) return;

      const currentX = event.touches[0].clientX;
      const currentY = event.touches[0].clientY;
      const diffX = currentX - this.touchStartX;
      const diffY = currentY - this.touchStartY;

      if (this.swipeDirection === null) {
        // Determine direction on the first significant move
        if (Math.abs(diffX) > 5 || Math.abs(diffY) > 5) {
          this.swipeDirection =
            Math.abs(diffX) > Math.abs(diffY) ? "horizontal" : "vertical";
        }
      }

      if (this.swipeDirection === "horizontal") {
        event.preventDefault(); // Prevent vertical scrolling
        this.translateX = diffX;
      }
    },

    handleTouchEnd() {
      if (!this.isSwiping) return;

      const swipeThreshold = 50; // Min pixels to trigger action

      if (
        this.swipeDirection === "horizontal" &&
        Math.abs(this.translateX) > swipeThreshold
      ) {
        if (this.translateX < 0) {
          // Swiped left
          this.loadNextSuggestedRecipe();
        } else if (this.translateX > 0) {
          // Swiped right
          this.loadPreviousSuggestedRecipe();
        }
      }

      // Reset for the next interaction
      this.isSwiping = false;
      this.translateX = 0;
      this.swipeDirection = null;
    },
  },
};
</script>

<style scoped>
.image-box {
  width: 100%;
  touch-action: pan-y; /* Allow vertical scroll by default */
  aspect-ratio: 1/1;
  display: flex;
  align-items: center;
  justify-content: center;

  /* Use Bootstrap variable or fallback */
  border-radius: var(--bs-border-radius, 10px);

  border-radius: 8px;
  overflow: hidden;
}

.image-recipe {
  width: 100%;
  object-fit: cover;
  border-radius: 10px;

  /* Animation from RecipeView */
  transition: transform 0.3s ease-in-out;
}

.image-recipe:hover {
  /* Animation from RecipeView */
  transform: scale(1.03);
}

.image-recipe.clickable,
h3.clickable {
  cursor: pointer;
}

.image-placeholder {
  border: 1px dashed var(--bs-border-color-translucent, #ccc);
  /* Styles for background image to cover the div */
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
}

/* Custom styles for .next-suggestion-btn are removed as btn-outline-secondary
   handles styling.
   If specific hover/disabled states are needed beyond Bootstrap's defaults for
   btn-outline-secondary, they can be added here. For now, we rely on Bootstrap.
*/
.btn-like {
  background: none;
  border: none;
  font-size: 1.5rem;
  padding: 0;
}
.next {
  margin-top: 5px;
  margin-right: -9px;
}
.next:hover {
  color: var(--bs-warning) !important;
}
</style>
